const DEFAULT_MAX_PARTICLES = 400;

/**
 * Pooled cosmetic particle bursts (hit sparks, dash poof, death explosion).
 * Pre-allocates a fixed pool and keeps every live particle packed into
 * `_all[0, _activeCount)` with the same swap-with-last-active compaction
 * ProjectilePool uses — bigger bursts (boss deaths, desperation finales)
 * now spawn zero garbage mid-fight. On exhaustion the particle closest to
 * death is recycled rather than dropping the new burst or growing the
 * array: particles are cosmetic, so graceful degradation beats a stutter.
 *
 * `burst()` stays call-compatible with the old un-pooled version — every
 * new field defaults to the old behavior:
 *  - shape: 'circle' (default) | 'spark' (motion-stretched streak along
 *    velocity) | 'square' (chunky debris)
 *  - additive: drawn in a second 'lighter'-composite pass (hot embers,
 *    energy flashes) — see Game.js's _drawParticles
 *  - angle + spread: directional cone instead of a full circle (impact
 *    sparks along a projectile's travel direction)
 *  - drag / gravity: per-second velocity damping / downward pull, for
 *    debris that slows and settles instead of flying straight forever
 */
export class ParticleSystem {
  constructor(maxParticles = DEFAULT_MAX_PARTICLES) {
    this._all = Array.from({ length: maxParticles }, () => ({
      x: 0, y: 0, vx: 0, vy: 0,
      life: 0, maxLife: 1,
      color: '#ffffff', size: 1,
      shape: 'circle', additive: false,
      drag: 0, gravity: 0,
    }));
    this._activeCount = 0;
  }

  burst(x, y, {
    count, color, speedMin = 20, speedMax = 80, life = 0.4, size = 1.5,
    shape = 'circle', additive = false, angle = null, spread = Math.PI * 2,
    drag = 0, gravity = 0,
  }) {
    for (let i = 0; i < count; i++) {
      const p = this._acquire();
      const dir = angle === null
        ? Math.random() * Math.PI * 2
        : angle + (Math.random() - 0.5) * spread;
      const speed = speedMin + Math.random() * (speedMax - speedMin);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(dir) * speed;
      p.vy = Math.sin(dir) * speed;
      p.life = life;
      p.maxLife = life;
      p.color = color;
      p.size = size;
      p.shape = shape;
      p.additive = additive;
      p.drag = drag;
      p.gravity = gravity;
    }
  }

  _acquire() {
    if (this._activeCount < this._all.length) {
      return this._all[this._activeCount++];
    }
    // Pool exhausted — steal the live particle nearest to expiring. O(n)
    // scan, but only ever paid in the rare over-budget frame.
    let steal = this._all[0];
    for (let i = 1; i < this._activeCount; i++) {
      if (this._all[i].life < steal.life) steal = this._all[i];
    }
    return steal;
  }

  update(dt) {
    for (let i = 0; i < this._activeCount; i++) {
      const p = this._all[i];
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.drag > 0) {
        const damp = Math.max(0, 1 - p.drag * dt);
        p.vx *= damp;
        p.vy *= damp;
      }
      if (p.gravity !== 0) p.vy += p.gravity * dt;
      p.life -= dt;
      if (p.life <= 0) {
        // Reclaim the slot: swap the last active particle in and re-check
        // this index (same compaction idiom as ProjectilePool.update).
        this._activeCount--;
        this._all[i] = this._all[this._activeCount];
        this._all[this._activeCount] = p;
        i--;
      }
    }
  }

  forEach(fn) {
    for (let i = 0; i < this._activeCount; i++) fn(this._all[i]);
  }

  get activeCount() {
    return this._activeCount;
  }
}
